import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import type { ProductListItem } from "../../shared/types";
import { useNative, useRail } from "../../shared/phone/media";
import { Split } from "../../shared/phone/Split";
import { Icon } from "../components/ui/Icon";
import { PageHeader } from "../components/ui/PageHeader";
import { ProductMark } from "./products/ProductMark";
import { ProductScreen, ProductsList } from "./products/ProductsNative";
import { useProducts } from "./products/useProducts";
import { useToast } from "./products/useToast";
import "../styles/products.css";
import { NewGoalMenu } from "../components/NewGoalMenu";
import { Can } from "../context/AuthContext";

function ProductCard({ p }: { p: ProductListItem }) {
  const page = `/products/${encodeURIComponent(p.id)}`;
  const flow = `${page}/profile`;
  const state = p.profile.state;
  const head = (
    <Link className="pc-h pc-open" to={page}>
      <ProductMark id={p.id} name={p.name} logo={!!p.logoR2Key} />
      <div>
        <h3>{p.name}</h3>
        {p.description && <p>{p.description}</p>}
      </div>
      <Icon name="right" />
    </Link>
  );

  if (state !== "confirmed") {
    const stopped = state === "none" || state === "failed";
    return (
      <article className="card pcard">
        {head}
        <div className="st-meta">
          {state === "learning" && <span className="chip auto">Learning the business</span>}
          {state === "question" && (
            <span className="chip you">
              <Icon name="user" />
              Waiting for your answer
            </span>
          )}
          {state === "draft" && (
            <span className="chip you">
              <Icon name="user" />
              Waiting for you to confirm
            </span>
          )}
          {state === "none" && <span className="chip">No profile yet</span>}
          {state === "failed" && <span className="chip">Learning stopped</span>}
        </div>
        <Can min="editor">
          <div className="m-act">
            <Link className="btn sm ghost" to={flow}>
              {stopped ? "Learn the business" : "Open"}
            </Link>
          </div>
        </Can>
      </article>
    );
  }

  return (
    <article className="card pcard">
      {head}
      <div className="st-meta">
        <span className="chip ok">
          <Icon name="check" />
          Profile confirmed
        </span>
        {p.goals > 0 ? (
          <Link className="chip link" to={page}>
            {p.goals} goal{p.goals > 1 ? "s" : ""}
            <Icon name="right" />
          </Link>
        ) : (
          <span className="chip">No goals yet</span>
        )}
      </div>
      <div className="m-act">
        <Can min="editor">
          <Link className="btn sm ghost" to={flow}>
            <Icon name="pen" />
            Profile
          </Link>
        </Can>
        <NewGoalMenu className="btn sm ghost" to={`${page}/goals/new`} coldEmail={p.coldEmail} />
      </div>
    </article>
  );
}

/** Your products (`/products`): each product with where its profile stands and its goals. */
export function Products() {
  const { items, err } = useProducts();
  const { toast, toastNode } = useToast();
  const navigate = useNavigate();
  const { state, pathname } = useLocation();
  const note = (state as { toast?: string } | null)?.toast;
  const native = useNative();
  const rail = useRail();

  // Why: a note handed over by the page before (a saved profile) shows once, not again on reload.
  useEffect(() => {
    if (!note) return;
    toast(note);
    navigate(pathname, { replace: true, state: null });
  }, [note, toast, navigate, pathname]);

  if (native) {
    // Why: on an iPad the first product opens beside the list, so the pane is never empty.
    const first = rail ? items?.[0]?.id : undefined;
    return (
      <>
        <Split
          list={<ProductsList items={items} err={err} selected={first} />}
          detail={first ? <ProductScreen id={first} split /> : <div className="pn-screen" />}
          showDetail={false}
        />
        {toastNode}
      </>
    );
  }

  return (
    <section className="wrap ga">
      <PageHeader
        title="Your products"
        action={
          <Can min="editor">
            <Link className="btn" to="/products/new">
              <Icon name="plus" />
              Add a product
            </Link>
          </Can>
        }
        sub="Every goal starts from a product profile you confirm."
      />
      {err && <p className="load-note">Couldn't load products: {err}</p>}
      {!items && !err && <p className="load-note">Loading…</p>}
      {items && items.length === 0 && (
        <section className="card">
          <p className="pd-none">No products yet. Add one, and the agent learns the business.</p>
        </section>
      )}
      {items && items.length > 0 && (
        <div className="plist">
          {items.map((p) => (
            <ProductCard key={p.id} p={p} />
          ))}
        </div>
      )}
      {toastNode}
    </section>
  );
}
